<template>
  <v-dialog
    v-model="opened"
    persistent
    light
    scrollable
    max-width="310"
  >
    <v-card v-if="currentDir !== null">
      <v-card-title>
        <v-list-item-action v-show="showGoBackButton">
          <v-btn icon @click.stop="goBack()">
            <v-icon color="black">chevron_left</v-icon>
          </v-btn>
        </v-list-item-action>
        <v-list-item-content>
          <v-list-item-title>{{ currentDir.name }}</v-list-item-title>
        </v-list-item-content>
        <v-list-item-action>
          <v-btn icon @click.stop="closeOnClickOutside">
            <v-icon color="black" size="17">close</v-icon>
          </v-btn>
        </v-list-item-action>
      </v-card-title>
      <v-divider />
      <v-card-text>
        <div class="list-directories">
          <div v-if="directories.length">
            <v-list-item
              v-for="directory in directories"
              :key="directory.path"
              class="list-tile-directory no-transition"
              :class="{ 'directory-selected' : isSelected(directory) }"
              @click.stop="selectDir(directory)"
              @dblclick="openDir(directory)"
            >
              <v-list-item-avatar size="30" class="align-content-center">
                <v-icon class="info white--text" size="17">folder</v-icon>
              </v-list-item-avatar>
              <v-list-item-content>
                <v-list-item-title>{{ directory.name }}</v-list-item-title>
              </v-list-item-content>
              <v-list-item-action>
                <v-tooltip right color="grey">
                  <template #activator="{ on }">
                    <v-btn icon ripple @click.stop="openDir(directory)" v-on="on">
                      <v-icon
                        :color="isSelected(directory) ? 'white' : 'black'"
                      >
                        chevron_right
                      </v-icon>
                    </v-btn>
                  </template>
                  <span>Open directory "{{ directory.name }}"</span>
                </v-tooltip>
              </v-list-item-action>
            </v-list-item>
          </div>
          <v-subheader v-else-if="!loading">There is no folders in this directory.</v-subheader>

          <!-- Loading -->
          <v-list-item v-if="loading" class="list-tile-directory">
            <v-list-item-content>
              <v-progress-circular
                :width="3"
                :size="30"
                indeterminate
                color="info"
              />
            </v-list-item-content>
          </v-list-item>
        </div>
      </v-card-text>
      <v-divider />
      <v-card-actions>
        <v-list-item class="list-tile-directory">
          <v-list-item-content :style="{ 'align-items' : 'flex-end' }">
            <v-tooltip v-if="item.copy" bottom color="grey">
              <template #activator="{ on }">
                <v-btn
                  small
                  color="info"
                  @click.stop="copy()"
                  v-on="on"
                >
                  Copie ici
                </v-btn>
              </template>
              <span>Copie "{{ item.wtitle }}" to directory "{{ selectedDir.name }}"</span>
            </v-tooltip>
            <v-tooltip v-else bottom color="grey">
              <template #activator="{ on }">
                <v-btn
                  small
                  color="info"
                  :disabled="moveButtonIsDisabled"
                  @click.stop="move()"
                  v-on="on"
                  v-html="moveButtonText"
                />
              </template>
              <span v-if="!moveButtonIsDisabled">Déplacer "{{ item.wtitle }}" vers le dossier "{{ selectedDir.name }}"</span>
            </v-tooltip>
          </v-list-item-content>
        </v-list-item>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useMutation, useQuery } from '@vue/apollo-composable';
import { useStore } from 'vue2-helpers/vuex';
import { useRouter, useRoute } from 'vue2-helpers/vue-router';

import { getBreadcrumbs } from '../../utils/content';
import { getGraphqlSchema } from '../../graphql/index';
import { EventBus } from '../../mixins';

// eslint-disable-next-line no-undef
const props = defineProps({
  closeParent: {
    type: Function,
    default: () => { }
  }
});

const store = useStore();

const router = useRouter();

const route = useRoute();

const selectedDir = ref(null); // Selected directory

const currentDir = ref(null); // Current opened directory

const directories = ref([]); // List of directories found in the current directory

const item = ref(null); // Item object

const itemDir = ref(null); // Item directory

const opened = ref(false); // If dialog is opened or not

const loading = ref(true); // Something is loading

const moving = ref(false);

const workspace = ref(null);

const path = ref(null);

const copying = ref(false);

const searchFoldersQueryEnabled = ref(false);

const breadcrumbs = computed(() => getBreadcrumbs(currentDir.value.path));

const showGoBackButton = computed(() => {
  if (currentDir.value) {
    return currentDir.value.path !== '/';
  }
  return false;
});

// eslint-disable-next-line arrow-body-style
const moveButtonIsDisabled = computed(() => {
  // If selected directory is the same as current directory of the item
  return selectedDir.value.path === itemDir.value.path || loading.value;
});

const moveButtonText = computed(() => {
  // If directory selected is the current directory opened
  if (selectedDir.value.path === currentDir.value.path) {
    return 'Déplacer ici';
  }
  // If it's other directory
  return 'Déplacer';
});

const isSelected = directory => {
  if (selectedDir.value.path === directory.path) {
    return true;
  }

  return false;
};

// Select a directory
const selectDir = directory => {
  if (selectedDir.value && selectedDir.value.path === directory.path) { // If user diselected a directory, sets the current directory has the selected directory
    selectedDir.value = currentDir.value;
  } else { // If user selected a directory
    selectedDir.value = directory;
  }
};

const close = () => {
  document.documentElement.style.overflow = 'auto';
  opened.value = false;
};

const closeOnClickOutside = () => {
  if (opened.value) {
    EventBus.$emit('unselectItem');
    close();
  }
};

const { mutate: copyMutation, onDone: onDoneCopy, onError: onErrorCopy } = useMutation(getGraphqlSchema('copyFile'), () => ({
  variables: {
    uuid: item.value.uuid,
    filename: item.value.wtitle,
    workspace: workspace.value,
    path: selectedDir.value.path
  }
}));

onDoneCopy(result => {
  const options = {
    closeable: true,
    timeout: 5000,
    type: 'success'
  };

  let text = `File "${item.value.wtitle}" copied!`;

  // Error on copy file
  if (!result.data.copyFile.uuid) {
    options.type = 'error';

    text = result.data.errors[0].message;
  } else if (workspace.value === store.getters.workspace.code) {
    router.push({
      name: 'ContentEditor',
      params: {
        wtype: result.data.copyFile.wtype,
        uuid: result.data.copyFile.uuid
      }
    });

    // clearCacheKeepAlive();
  }

  copying.value = false;
  // Send a notification event
  EventBus.$emit('show-notification', { text, options });
});

onErrorCopy(error => {
  const options = {
    closeable: true,
    timeout: 4000,
    type: 'error'
  };
  const text = error.message;
  copying.value = false;
  // Send a notification event
  EventBus.$emit('show-notification', { text, options });
});

const copy = () => {
  copying.value = true;

  const options = {
    timeout: 8000,
    type: 'info'
  };

  const text = `Copying file "${item.value.wtitle}"`;

  EventBus.$emit('show-notification', { text, options });

  copyMutation();

  close();

  props.closeParent();
};

const { mutate: moveMutation, onDone: onDoneMove, onError: onErrorMove } = useMutation(getGraphqlSchema('moveFile'), () => ({
  variables: {
    uuid: item.value.uuid,
    path: selectedDir.value.path
  }
}));

onDoneMove(result => {
  const options = {
    closeable: true,
    timeout: 5000,
    type: 'success'
  };

  let text = `"${item.value.wtitle}" moved!`;

  // Error on rename the Folder
  if (!result.data.moveFile.success) {
    options.type = 'error';
    text = result.data.moveFile.message;
  } else {
    EventBus.$emit('removeItem', item.value);
    EventBus.$emit('unselectItem');

    if (['ContentEditor'].includes(route.name)) {
      EventBus.$emit('moveItemTo', selectedDir.value.path);
    }
  }

  moving.value = false;

  // Send a notification event
  EventBus.$emit('show-notification', { text, options });
});

onErrorMove(error => {
  const options = {
    closeable: true,
    timeout: 4000,
    type: 'error'
  };

  const text = error.message;
  moving.value = false;

  // Send a notification event
  EventBus.$emit('show-notification', { text, options });
});

const move = () => {
  moving.value = true;

  const options = {
    timeout: 8000,
    type: 'info'
  };

  const text = `Moving "${item.value.wtitle}" to folder "${selectedDir.value.name}"`;

  EventBus.$emit('show-notification', { text, options });
  moveMutation();

  close();
};

const openDir = directory => {
  selectedDir.value = directory;

  currentDir.value = directory;

  directories.value = [];

  loading.value = true;

  searchFoldersQueryEnabled.value = true;
};

const locale = computed(() => {
  const workspaceConfig = store.getters.workspaces.find(ws => ws.code === workspace.value);

  if (!workspaceConfig || !workspaceConfig.settings.languages) {
    return 'fr';
  }

  return workspaceConfig.settings.languages && workspaceConfig.settings.languages.allowedLocales.includes(store.state.locale) ? store.state.locale : workspaceConfig.settings.languages.default;
});

const searchFoldersVariables = computed(() => {
  const variablePath = selectedDir.value ? selectedDir.value.path : '';
  return {
    input: {
      from: 0,
      size: 1000,
      query: {
        bool: {
          filter: [
            { terms: { wtype: ['folder'] } },
            { terms: { 'path.raw': [variablePath] } },
            { terms: { workspace: [workspace.value] } }
          ]
        }
      },
      sort: [
        { 'wtitle.raw': 'asc' }
      ]
    }
  };
});

// Only speedboat needs localized verions
const openSearchQueryContext = computed(() => (store.state.environment.PROJECT === 'speedboat' ? { context: { headers: { locale: locale } } } : {}));

const { onResult: onResultSearch, loading: searchLoading } = useQuery(
  getGraphqlSchema('openSearch'),
  searchFoldersVariables,
  () => ({
    enabled: searchFoldersQueryEnabled.value,
    fetchPolicy: 'no-cache',
    ...openSearchQueryContext.value
  })
);

onResultSearch(result => {
  // For when it is loading the results
  if (searchLoading.value) {
    return;
  }
  searchFoldersQueryEnabled.value = false;
  loading.value = false;

  if (result.data.search) {
    directories.value = [];

    result.data.search.items.forEach(element => {
      let base_path = element.path;

      if (!element.path.endsWith('/')) {
        base_path += '/';
      }

      const newItem = {
        name: element.wtitle,
        path: base_path + element.wtitle
      };

      directories.value.push(newItem);
    });
  }
});

const goBack = () => {
  const thisItem = breadcrumbs.value.length > 1 ? breadcrumbs.value.length - 2 : breadcrumbs.value.length - 1;
  openDir(breadcrumbs.value[thisItem]);
};

const open = newItem => {
  // Disables scroll on the page
  document.documentElement.style.overflow = 'hidden';

  // Opens dialog box
  opened.value = true;

  // Set's the item to be moved
  item.value = newItem;

  if (item.value.copy) {
    workspace.value = item.value.workspace;
  } else {
    workspace.value = store.getters.workspace.code;
  }

  if (workspace.value === store.getters.workspace.code) {
    path.value = item.value.path;
  } else {
    path.value = '/';
  }

  // Opens the current directory of the item
  const itemBreadcrumbs = getBreadcrumbs(path.value);
  itemDir.value = itemBreadcrumbs[itemBreadcrumbs.length - 1];
  openDir(itemDir.value);
};

// eslint-disable-next-line no-undef
defineExpose({ open });

</script>

<style lang="scss" scoped>
  .v-card__text {
      padding: 0px !important;
  }

  .v-card__title {
    padding: 5px 15px !important;
  }

  .list-tile-directory {
      height: 50px;

      a {
          cursor: context-menu !important;
      }

      .v-list__tile {
          height: 50px;

          .v-list__tile__title {
              font-size: 15px;
          }

      }

      &.directory-selected {
          background-color: #2196f3;
          color: #fff;
      }
  }

  .list-directories{
    height: 300px;
  }

  .v-card {
    overflow: hidden;
  }
</style>
